import type {
	CatalogRule,
	ClassifierCategory,
	Filter,
	PartialPolicyIn,
	PiiScanTemplateType,
	PolicyFramework,
	PolicyOut,
	PolicyRuleIn,
	PolicyRuleOut,
	PolicySeverity,
	PolicyTemplate,
} from '@repo/api-codegen';
import { makeAutoObservable } from 'mobx';
import { getPolicyPiiScanBannerInformation } from '../utils/policyPiiScanBanner';

export class CreatePolicyStore {
	// Properties
	name: string = '';
	description: string = '';
	frameworks: string[] = [];
	severity: PolicySeverity = 'NONE';

	// Errors
	nameError: string = '';
	descriptionError: string = '';
	severityError: string = '';
	remediationError: string = '';
	ownersError: string = '';
	subscribersError: string = '';

	// Remediation
	remediationGuidelines: string = '';
	owners: string[] = [];
	ownersGroups: string[] = [];
	subscribers: string[] = [];
	subscribersGroups: string[] = [];
	automationId: string | undefined = undefined;
	automationName: string | undefined = undefined;
	createdAutomationId: string | undefined = undefined;

	// Resource selection
	resourceRule: CatalogRule = {
		operator: 'and',
		operands: [],
	};

	// Conditions - legacy support
	policyRule: CatalogRule = {
		operator: 'and',
		operands: [],
	};
	policyRuleError: string = '';
	ruleName: string = '';

	// Conditions - new PolicyRule support
	policyRules: PolicyRuleIn[] = [];
	policyRulesError: string = '';
	initialPolicyRules: PolicyRuleOut[] = [];

	// Loading states for policy rules operations
	isLoadingPolicyRules: boolean = false;
	isSavingPolicyRules: boolean = false;

	// Track rule changes for server sync
	modifiedRuleIndices: Set<number> = new Set();
	deletedRuleIds: string[] = [];

	// PII classifier
	piiClassifierAdded: boolean = false;
	piiClassifierInstructions: string = '';
	displayPiiClassifierBanner: boolean = false;
	piiClassifierBannerTitle: string = '';
	piiClassifierBannerMessage: string = '';

	initialState: Partial<PolicyOut> | undefined;
	initialAutomationName: string | undefined = undefined;

	constructor({
		policy,
		policyTemplate,
		policyRules,
		owners,
		subscribers,
		piiScanMetadata,
	}: {
		policy?: Partial<PolicyOut>;
		policyTemplate?: PolicyTemplate;
		policyRules?: PolicyRuleOut[];
		owners?: string[];
		subscribers?: string[];
		piiScanMetadata?: {
			templateTitle: PiiScanTemplateType;
			templatePiiClassifiers: ClassifierCategory[];
		};
	}) {
		this.initialState = policy;
		this.name = policy?.name ?? '';
		this.description = policy?.description ?? '';
		this.frameworks = policy?.frameworks ?? [];
		this.severity = policy?.severity ?? 'NONE';
		this.resourceRule = policy?.resource_rule ?? {
			operator: 'and',
			operands: [],
		};
		this.policyRule = policy?.policy_rule ?? {
			operator: 'and',
			operands: [
				{
					operator: 'and',
					operands: [],
				},
			],
		};

		this.remediationGuidelines = policy?.remediation ?? '';
		this.owners = policy?.owners ?? [];
		this.ownersGroups = policy?.owners_groups ?? [];
		this.subscribers = policy?.subscribers ?? [];
		this.subscribersGroups = policy?.subscribers_groups ?? [];
		this.automationId = policy?.automation_id ?? undefined;
		this.ruleName = '';

		// Normalize CatalogRule by removing extra fields (field, value, sub_value) that come from the API
		// but aren't needed for the store. This ensures consistent comparisons in hasUnsavedChanges.
		const normalizeRule = (rule: CatalogRule): CatalogRule => ({
			operator: rule.operator,
			operands: rule.operands,
		});

		// Initialize policy rules from existing data
		if (policyRules) {
			this.initialPolicyRules = policyRules;
			this.policyRules = policyRules.map((rule) => ({
				name: rule.name,
				type: rule.type,
				description: rule.description,
				weight: rule.weight,
				points_possible: rule.points_possible,
				filter: rule.filter,
				classifiers: rule.classifiers ?? [],
				llm_instructions: rule.llm_instructions ?? '',
			}));
			this.piiClassifierAdded = this.policyRules.some(
				(rule) => rule.type === 'PII_SCAN'
			);
		}

		if (owners) {
			this.owners = [...this.owners, ...owners];
		}
		if (subscribers) {
			this.subscribers = [...this.subscribers, ...subscribers];
		}

		if (piiScanMetadata) {
			const newPolicyRule: PolicyRuleIn = {
				name: `Rule ${this.policyRules.length + 1}`,
				description: '',
				weight: 1,
				points_possible: 10,
				type: 'PII_SCAN',

				filter: {
					operator: 'and',
					operands: [
						{
							operator: 'and',
							operands: [],
						},
					],
				},
				classifiers: piiScanMetadata.templatePiiClassifiers,
			};
			this.policyRules.push(newPolicyRule);
			this.piiClassifierAdded = true;
			this.displayPiiClassifierBanner = true;
			const { title, message } = getPolicyPiiScanBannerInformation(
				piiScanMetadata.templateTitle
			);
			this.piiClassifierBannerTitle = title;
			this.piiClassifierBannerMessage = message;
		}

		if (policyTemplate) {
			this.name = policyTemplate.name;
			this.description = policyTemplate.description ?? '';
			this.frameworks = policyTemplate.frameworks ?? [];
			this.severity = policyTemplate.severity ?? 'NONE';
			this.resourceRule = normalizeRule(policyTemplate.resource_rule);
			this.policyRule = normalizeRule(policyTemplate.policy_rule);
			this.remediationGuidelines = policyTemplate.remediation ?? '';

			// Handle rule_title parameter for migration to multiple policy rules
			const templateWithRuleTitle = policyTemplate as PolicyTemplate & {
				rule_title?: string;
			};
			if (templateWithRuleTitle.rule_title && !policyRules) {
				// Only create policy rule from template if no existing policy rules
				// Create a policy rule entry with the same catalog filter and rule title
				const templateRule: PolicyRuleIn = {
					name: templateWithRuleTitle.rule_title,
					description: '',
					weight: 1,
					points_possible: 1,
					filter: policyTemplate.policy_rule,
				};
				this.policyRules = [templateRule];

				// Also add to initialPolicyRules to avoid false hasUnsavedChanges
				// Create a PolicyRuleOut-like object with placeholder values for required fields
				// If templateRule doesn't have type, we omit it from the object so normalization matches correctly
				const initialRule: PolicyRuleOut = {
					id: '', // Placeholder - not used in comparison
					created_at: '', // Placeholder - not used in comparison
					updated_at: '', // Placeholder - not used in comparison
					scoring_type: 'weighted', // Placeholder - not used in comparison
					type: templateRule.type ?? 'METADATA', // Use template type or default to satisfy PolicyRuleOut requirement
					name: templateRule.name,
					description: templateRule.description ?? null,
					weight: templateRule.weight ?? null,
					points_possible: templateRule.points_possible ?? null,
					filter: templateRule.filter,
					classifiers: templateRule.classifiers ?? null,
					llm_instructions: templateRule.llm_instructions ?? null,
				};

				// If template rule doesn't have type, we need to mark it so normalization excludes type
				// We'll store the original template rule's type status and use it in normalization
				// For now, we'll create the object and handle type exclusion in normalization
				this.initialPolicyRules = [initialRule];
			}

			// Treat template values as initial state to avoid false hasUnsavedChanges
			this.initialState = {
				name: policyTemplate.name,
				description: policyTemplate.description ?? '',
				frameworks: policyTemplate.frameworks ?? [],
				severity: policyTemplate.severity ?? 'NONE',
				resource_rule: normalizeRule(policyTemplate.resource_rule),
				policy_rule: normalizeRule(policyTemplate.policy_rule),
				remediation: policyTemplate.remediation ?? '',
				owners: [],
				owners_groups: [],
				subscribers: [],
				subscribers_groups: [],
			};
		}

		makeAutoObservable(this);
	}

	async cleanupOrphanedAutomation(
		deleteAutomation: (params: { id: string }) => Promise<unknown>
	) {
		if (this.createdAutomationId) {
			try {
				await deleteAutomation({ id: this.createdAutomationId });
			} catch {
				// Fail silently
			}
		}
	}

	get hasUnsavedChanges() {
		const policy = this.initialState;
		// Normalize both policyRules and initialPolicyRules to the same format for comparison
		// Match rules by index and only include type if the corresponding PolicyRuleIn has it
		const currentRulesNormalized = this.policyRules.map((rule) => {
			const normalized: {
				name: string | null;
				type?: string;
				description: string | null;
				weight: number | null;
				points_possible: number | null;
				filter: Filter;
				classifiers: ClassifierCategory[];
				llm_instructions: string;
			} = {
				name: rule.name ?? null,
				description: rule.description ?? null,
				weight: rule.weight ?? null,
				points_possible: rule.points_possible ?? null,
				filter: rule.filter,
				classifiers: rule.classifiers ?? [],
				llm_instructions: rule.llm_instructions ?? '',
			};

			// Only include type if the PolicyRuleIn has it
			if (rule.type !== undefined && rule.type !== null) {
				normalized.type = rule.type;
			}

			return normalized;
		});

		const initialRulesNormalized = this.initialPolicyRules.map(
			(rule, index) => {
				const currentRule = this.policyRules[index];
				const normalized: {
					name: string | null;
					type?: string;
					description: string | null;
					weight: number | null;
					points_possible: number | null;
					filter: Filter;
					classifiers: ClassifierCategory[];
					llm_instructions: string;
				} = {
					name: rule.name ?? null,
					description: rule.description ?? null,
					weight: rule.weight ?? null,
					points_possible: rule.points_possible ?? null,
					filter: rule.filter,
					classifiers: rule.classifiers ?? [],
					llm_instructions: rule.llm_instructions ?? '',
				};

				// Only include type if the corresponding PolicyRuleIn has it (to match normalization above)
				if (
					currentRule &&
					currentRule.type !== undefined &&
					currentRule.type !== null
				) {
					normalized.type = rule.type;
				}

				return normalized;
			}
		);

		if (!policy) {
			const defaultPolicyRule = {
				operator: 'and',
				operands: [{ operator: 'and', operands: [] }],
			};
			return (
				this.name !== '' ||
				this.description !== '' ||
				this.frameworks.length > 0 ||
				this.severity !== 'NONE' ||
				this.remediationGuidelines !== '' ||
				this.resourceRule.operands.length > 0 ||
				JSON.stringify(this.policyRule) !== JSON.stringify(defaultPolicyRule) ||
				JSON.stringify(currentRulesNormalized) !==
					JSON.stringify(initialRulesNormalized) ||
				this.automationId !== undefined
			);
		}

		return (
			this.name !== policy.name ||
			this.description !== (policy.description ?? '') ||
			JSON.stringify(this.frameworks) !==
				JSON.stringify(policy.frameworks ?? []) ||
			this.severity !== (policy.severity ?? 'NONE') ||
			this.remediationGuidelines !== (policy.remediation ?? '') ||
			JSON.stringify(this.resourceRule) !==
				JSON.stringify(
					policy.resource_rule ?? { operator: 'and', operands: [] }
				) ||
			JSON.stringify(this.policyRule) !==
				JSON.stringify(
					policy.policy_rule ?? { operator: 'and', operands: [] }
				) ||
			JSON.stringify(currentRulesNormalized) !==
				JSON.stringify(initialRulesNormalized) ||
			JSON.stringify(this.owners) !== JSON.stringify(policy.owners ?? []) ||
			JSON.stringify(this.ownersGroups) !==
				JSON.stringify(policy.owners_groups ?? []) ||
			JSON.stringify(this.subscribers) !==
				JSON.stringify(policy.subscribers ?? []) ||
			JSON.stringify(this.subscribersGroups) !==
				JSON.stringify(policy.subscribers_groups ?? []) ||
			this.automationId !== (policy.automation_id ?? undefined)
		);
	}

	setName = (name: string) => {
		this.name = name;
		this.nameError = '';
	};

	setDescription = (description: string) => {
		this.description = description;
		this.descriptionError = '';
	};

	setFrameworks = (frameworks: string[]) => {
		this.frameworks = frameworks;
	};

	setSeverity = (severity: PolicySeverity) => {
		this.severity = severity;
		this.severityError = '';
	};

	// PII classifier
	setPIIClassifierAdded = (added: boolean) => {
		this.piiClassifierAdded = added;
		this.policyRulesError = '';
	};

	setPIIClassifierBoolean = (
		index: number,
		field: ClassifierCategory,
		value: boolean
	) => {
		if (value) {
			if (!this.policyRules[index].classifiers) {
				this.policyRules[index].classifiers = [];
			}
			// Only add if not already present
			if (!this.policyRules[index].classifiers?.includes(field)) {
				this.policyRules[index].classifiers?.push(field);
			}
		} else {
			this.policyRules[index].classifiers = this.policyRules[
				index
			].classifiers?.filter((c: ClassifierCategory) => c !== field);
		}
	};

	setPIIClassifierInstructions = (index: number, instructions: string) => {
		this.policyRules[index].llm_instructions = instructions;
	};
	// Remediation

	setRemediationGuidelines = (guidelines: string) => {
		this.remediationGuidelines = guidelines;
		this.remediationError = '';
	};

	setOwners = (owners: string[]) => {
		this.owners = owners;
		this.ownersError = '';
		const combined = [...this.subscribers, ...owners];
		this.subscribers = [...new Set(combined)];
		this.subscribersError = '';
	};

	setOwnersGroups = (ownersGroups: string[]) => {
		this.ownersGroups = ownersGroups;
		this.ownersError = '';
		const combined = [...this.subscribersGroups, ...ownersGroups];
		this.subscribersGroups = [...new Set(combined)];
		this.subscribersError = '';
	};

	get dqsEnabled() {
		return (
			this.frameworks.includes('DQS') || this.initialState?.dqs_enabled || false
		);
	}

	setSubscribers = (subscribers: string[]) => {
		this.subscribers = subscribers;
		this.subscribersError = '';
	};

	setSubscribersGroups = (subscribersGroups: string[]) => {
		this.subscribersGroups = subscribersGroups;
		this.subscribersError = '';
	};

	setValue = (field: 'owners' | 'subscribers') => (value: string[]) => {
		if (field === 'owners') {
			this.setOwners(value);
		} else {
			this.setSubscribers(value);
		}
	};

	setResourceRule = (
		resourceRule: CatalogRule,
		isUserInitiated: boolean = false
	) => {
		// Normalize to remove extra fields (field, value, sub_value) from the top-level rule only.
		// The nested operands keep their fields intact since they're needed for filtering.
		const normalizedNewRule: CatalogRule = {
			operator: resourceRule.operator,
			operands: resourceRule.operands,
		};

		// Normalize current rule for comparison
		const normalizedCurrentRule: CatalogRule = {
			operator: this.resourceRule.operator,
			operands: this.resourceRule.operands,
		};

		const rulesAreEqual =
			JSON.stringify(normalizedNewRule) ===
			JSON.stringify(normalizedCurrentRule);

		// Only update if:
		// 1. The rule actually changed AND
		// 2. It's a user-initiated change (not programmatic initialization)
		// This prevents false hasUnsavedChanges when FilterBlockController initializes
		if (!rulesAreEqual && isUserInitiated) {
			this.resourceRule = normalizedNewRule;
		}
	};

	// Legacy policy rule support
	setPolicyRule = (policyRule: CatalogRule) => {
		// Normalize to remove extra fields (field, value, sub_value) from the top-level rule only.
		// The nested operands keep their fields intact since they're needed for filtering.
		this.policyRule = {
			operator: policyRule.operator,
			operands: policyRule.operands,
		};
	};

	// New PolicyRule support
	setPolicyRules = (policyRules: PolicyRuleIn[]) => {
		this.policyRules = policyRules;
		this.policyRulesError = '';
	};

	addPolicyRule = (policyRule: PolicyRuleIn) => {
		this.policyRules.push(policyRule);
		this.policyRulesError = '';
	};

	removePolicyRule = (index: number) => {
		const existingRule = this.initialPolicyRules[index];

		// If this is an existing rule, track it for deletion
		if (existingRule?.id) {
			this.deletedRuleIds.push(existingRule.id);
		}

		this.policyRules.splice(index, 1);
		this.modifiedRuleIndices.delete(index);

		// Update indices for remaining rules
		const newIndices = new Set<number>();
		for (const idx of this.modifiedRuleIndices) {
			if (idx > index) {
				newIndices.add(idx - 1);
			} else {
				newIndices.add(idx);
			}
		}
		this.modifiedRuleIndices = newIndices;

		this.policyRulesError = '';
	};

	updatePolicyRule = (index: number, policyRule: PolicyRuleIn) => {
		this.policyRules[index] = policyRule;
		this.modifiedRuleIndices.add(index);
		this.policyRulesError = '';
	};

	// Server sync methods
	setLoadingPolicyRules = (loading: boolean) => {
		this.isLoadingPolicyRules = loading;
	};

	setSavingPolicyRules = (saving: boolean) => {
		this.isSavingPolicyRules = saving;
	};

	// Get rules that need to be created (new rules)
	getNewRules = (): { rule: PolicyRuleIn; tempIndex: number }[] =>
		this.policyRules
			.map((rule, index) => ({ rule, tempIndex: index }))
			.filter((_, index) => index >= this.initialPolicyRules.length);

	// Get rules that need to be updated (existing rules that were modified)
	getModifiedRules = (): {
		rule: PolicyRuleIn;
		ruleId: string;
		tempIndex: number;
	}[] =>
		Array.from(this.modifiedRuleIndices)
			.filter((index) => index < this.initialPolicyRules.length)
			.map((index) => ({
				rule: this.policyRules[index],
				ruleId: this.initialPolicyRules[index].id,
				tempIndex: index,
			}));

	// Get rule IDs that need to be deleted
	getDeletedRuleIds = (): string[] => [...this.deletedRuleIds];

	// Clear change tracking after successful save
	clearChangeTracking = () => {
		this.modifiedRuleIndices.clear();
		this.deletedRuleIds = [];
	};

	// Update initial state after successful save
	updateInitialPolicyRules = (policyRules: PolicyRuleOut[]) => {
		this.initialPolicyRules = policyRules;
		this.clearChangeTracking();
	};

	setAutomationName = (name: string) => {
		this.automationName = name;
	};

	setAutomationId = (id: string | undefined) => {
		this.automationId = id;
	};

	setCreatedAutomationId = (id: string | undefined) => {
		this.createdAutomationId = id;
		this.automationId = id;
	};

	setAutomationData = (automation: { title?: string }) => {
		if (automation?.title) {
			this.automationName = automation.title;
			this.initialAutomationName = automation.title;
		}
	};

	setRuleName = (name: string) => {
		this.ruleName = name;
	};

	get isFormValid() {
		this.nameError = '';
		this.ownersError = '';
		this.policyRuleError = '';
		this.policyRulesError = '';

		if (!this.name) {
			this.nameError = 'Name is required';
		}

		if (!this.description) {
			this.descriptionError = 'Description is required';
		}

		if (!this.severity || this.severity === 'NONE') {
			this.severityError = 'Severity is required';
		}

		if (!this.remediationGuidelines) {
			this.remediationError = 'Remediation guidelines are required';
		}

		if (this.owners.length === 0) {
			this.ownersError = 'Owner is required';
		}

		if (this.subscribers.length === 0) {
			this.subscribersError = 'Subscribers are required';
		}

		// Check if there are any policy rules at all
		if (this.policyRules.length === 0 && !this.piiClassifierAdded) {
			this.policyRulesError = 'At least one policy condition is required';

			// Also check legacy policy rule for backwards compatibility
			if (!this.policyRule || this.policyRule.operands.length === 0) {
				this.policyRuleError = 'At least one policy filter is required';
			}
			// If metadata condition is added, but no filters are added, show error
		} else if (
			!this.piiClassifierAdded &&
			this.policyRules.every((rule) => rule.filter.operands.length === 0)
		) {
			this.policyRulesError = 'At least one metadata filter is required';
		} else {
			// If we have new policy rules, we don't need to validate legacy rules
			this.policyRuleError = '';
			this.policyRulesError = '';
		}

		return !(
			this.nameError ||
			this.ownersError ||
			this.policyRuleError ||
			this.policyRulesError ||
			this.descriptionError ||
			this.severityError ||
			this.remediationError ||
			this.subscribersError
		);
	}

	getData(): PartialPolicyIn {
		return {
			name: this.name,
			description: this.description,
			frameworks: this.frameworks as PolicyFramework[],
			severity: this.severity,
			owners: this.owners,
			owners_groups: this.ownersGroups,
			subscribers: this.subscribers,
			subscribers_groups: this.subscribersGroups,
			resource_rule: this.resourceRule,
			policy_rule: this.policyRule,
			remediation: this.remediationGuidelines,
			automation_id: this.automationId,
		};
	}

	getPolicyRules(): PolicyRuleIn[] {
		return this.policyRules;
	}
}
